import Player from '@vimeo/player';
import { throttle } from 'throttle-debounce';
import { addEvent } from './eventBus';

const MOBILE_BREAKPOINT = 768;
const VIMEO_STATE_PLAYING = 'playing';
const VIMEO_STATE_PAUSED = 'paused';
const VIMEO_STATE_STOPPED = 'stopped';

const getIframeWidthFromRatio = (videoWidthRatio: number): number => {
  const windowInnerWidth = document.body && document.body.clientWidth ? document.body.clientWidth : window.innerWidth;
  return windowInnerWidth > MOBILE_BREAKPOINT ? (windowInnerWidth * videoWidthRatio) : windowInnerWidth;
}

const initVimeoVideo = (): void => {
  const videoElements = document.querySelectorAll('[data-vimeo-video]');
  if (!videoElements.length) return;

  videoElements.forEach((videoEl: HTMLDivElement) => {
    const container = videoEl.parentElement as HTMLDivElement;
    const overlay = container.querySelector('[data-vimeo-overlay]');
    const playButton = container.querySelector('[data-vimeo-play]');
    const pauseButton = container.querySelector('[data-vimeo-pause]');
    const muteButton = container.querySelector('[data-vimeo-mute]');
    const fullscreenButton = container.querySelector('[data-vimeo-fullscreen]');
    const closeButton = container.querySelector('[data-vimeo-close]');
    const videoWidthRatio = parseFloat(videoEl.dataset.widthRatio);
    let currentVolume = 1;
    const iframeWidth = getIframeWidthFromRatio(videoWidthRatio);

    // Video Setup
    const {
      autoplay,
      videoUrl,
      controls
    } = videoEl.dataset;

    const options = {
      url: videoUrl,
      controls,
      width: iframeWidth,
      title: false,
    };

    const videoPlayer = new Player(videoEl, options);
    addEvent('modalopen', ({ modal }) => {
      // This should only be used in the homepage, so I will hide the play button while video is buffering.
      container.dataset.state = VIMEO_STATE_PLAYING;
      if (modal === 'hero-video') {
        videoPlayer.play()
      }
    })
    addEvent('modalclose', () => videoPlayer.pause())
    videoPlayer.on('play', function () {
      container.dataset.state = VIMEO_STATE_PLAYING;
    });
    videoPlayer.on('pause', function () {
      container.dataset.state = VIMEO_STATE_PAUSED;
    });
    videoPlayer.on('ended', function () {
      container.dataset.state = VIMEO_STATE_STOPPED;
    });


    // Custom video controls
    if (playButton) {      
      const play = () => {
        videoPlayer.play()
      }

      playButton.addEventListener('click', () => play());
      overlay.addEventListener('click', (e) => {
        if (overlay !== e.target) { e.preventDefault() }
        if (container.dataset.state === VIMEO_STATE_PAUSED || container.dataset.state === VIMEO_STATE_STOPPED)
          play()
        else
          videoPlayer.pause()
      });
      pauseButton.addEventListener('click', () => videoPlayer.pause());
      fullscreenButton.addEventListener('click', (e) => {
        e.stopPropagation();
        container.classList.toggle('fullscreen');
      });
      closeButton.addEventListener('click', (e) => {
        e.stopPropagation();
        container.classList.remove('fullscreen');
      });
      muteButton.addEventListener('click', (e) => {
        e.stopPropagation();
        currentVolume = currentVolume === 0 ? 1 : 0;
        videoPlayer.setVolume(currentVolume);
        container.classList.toggle('muted');
      });
    }

    // if we have auto play set, then crack on and play the video
    if (autoplay) {
      videoPlayer.play();
    }
  });

  window.addEventListener(
    'resize',
    throttle(
      250,
      false,
      () => {
        const iframes = document.querySelectorAll('[data-vimeo-video] iframe');
        iframes.forEach((iframe: HTMLIFrameElement) => {
          const videoWidthRatio = parseFloat(iframe.parentElement.dataset.widthRatio);
          const iframeWidth = getIframeWidthFromRatio(videoWidthRatio);
          iframe.setAttribute('width', iframeWidth + '');
          iframe.setAttribute('height', (iframeWidth / 1.77) + ''); // 16/9
        })
      }
    )
  );

  window.addEventListener('keydown', function (event) {
    if (event.defaultPrevented) {
      return; // Do nothing if the event was already processed
    }

    const els = document.querySelectorAll('[data-vimeo-video]');
    switch (event.key) {
      case 'Esc': // IE/Edge specific value
      case 'Escape':
        els.forEach((el) => {
          el.parentElement.classList.remove('fullscreen');
        });
        break;
      default:
        return;
    }

    event.preventDefault();
  }, true);


}

export {
  initVimeoVideo
}